import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getGames, deleteGame } from '../services/games';
import { GameWithPlayers } from '../types';
import { supabase } from '../lib/supabase';
import { getPlayerDisplayName } from '../utils/playerDisplay';
import Loading from '../components/Loading';
import './GamesList.css';

interface GameWithActualScores extends GameWithPlayers {
  actualFramesPlayerA: number;
  actualFramesPlayerB: number;
}

const GamesList: React.FC = () => {
  const { t } = useTranslation();
  const [games, setGames] = useState<GameWithActualScores[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadGames();
  }, []);

  const loadGames = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('GamesList: Starting to load games...');
      const gamesData = await getGames();
      console.log('GamesList: Games loaded successfully:', gamesData);
      
      // Batch load all frames for all games at once (much faster)
      const gameIds = gamesData.map(g => g.id);
      const allFramesMap = await batchLoadFrames(gameIds);
      
      // Calculate actual scores for each game using cached frames
      const gamesWithActualScores = gamesData.map((game) => {
        const frames = allFramesMap.get(game.id) || [];
        const actualPlayerA = frames.filter(f => f.playerAFinalScore > f.playerBFinalScore).length;
        const actualPlayerB = frames.filter(f => f.playerBFinalScore > f.playerAFinalScore).length;
        
        return {
          ...game,
          actualFramesPlayerA: actualPlayerA,
          actualFramesPlayerB: actualPlayerB
        };
      });
      
      setGames(gamesWithActualScores);
    } catch (err) {
      console.error('GamesList: Error loading games:', err);
      setError(err instanceof Error ? err.message : t('errors.failedToLoadGames'));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Batch load all completed frames for multiple games at once
   * This is MUCH faster than loading frames for each game individually
   */
  const batchLoadFrames = async (gameIds: number[]): Promise<Map<number, any[]>> => {
    if (gameIds.length === 0) return new Map();
    
    try {
      const { data: allFrames, error: framesError } = await supabase
        .from('frames')
        .select('gameId, playerAFinalScore, playerBFinalScore')
        .in('gameId', gameIds)
        .not('playerAFinalScore', 'is', null)
        .not('playerBFinalScore', 'is', null);

      if (framesError) {
        console.error('Error batch loading frames:', framesError);
        return new Map();
      }

      // Group frames by gameId
      const framesMap = new Map<number, any[]>();
      allFrames?.forEach(frame => {
        if (!framesMap.has(frame.gameId)) {
          framesMap.set(frame.gameId, []);
        }
        framesMap.get(frame.gameId)!.push(frame);
      });
      
      return framesMap;
    } catch (err) {
      console.error('Failed to batch load frames:', err);
      return new Map();
    }
  };

  const isGameFinished = (game: GameWithActualScores) => {
    // Game is finished when one player has won more than half of the frames
    const totalFramesPlayed = game.actualFramesPlayerA + game.actualFramesPlayerB;
    const framesToWin = Math.ceil(game.framesToPlay / 2);
    
    return game.actualFramesPlayerA >= framesToWin || 
           game.actualFramesPlayerB >= framesToWin || 
           totalFramesPlayed >= game.framesToPlay;
  };

  const handleDeleteGame = async (gameId: number) => {
    try {
      setDeleting(true);
      setError(null);
      await deleteGame(gameId);
      setDeleteConfirm(null);
      // Reload games to reflect the deletion
      await loadGames();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete game');
    } finally {
      setDeleting(false);
    }
  };

  const confirmDelete = (gameId: number) => {
    setDeleteConfirm(gameId);
  };

  const cancelDelete = () => {
    setDeleteConfirm(null);
  };

  const activeGames = games.filter(game => !isGameFinished(game));
  const finishedGames = games.filter(game => isGameFinished(game));

  if (loading) return <Loading />;

  return (
    <div className="games-container">
      <div className="games-header">
        <h1>{t('games.title')}</h1>
        <Link to="/new-game" className="btn btn-primary">
          {t('games.newGame')}
        </Link>
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
          <button onClick={loadGames} className="btn btn-secondary">
            {t('common.retry')}
          </button>
        </div>
      )}

      <div className="games-section">
        <h2>{t('games.activeGames')}</h2>
        {activeGames.length === 0 ? (
          <p className="no-games">{t('games.noActiveGames')}</p>
        ) : (
          <div className="games-grid">
            {activeGames.map(game => (
              <div key={game.id} className="game-card">
                <Link to={`/game/${game.id}`} className="game-link">
                  <div className="game-players">
                    <span className={game.playerA.is_anonymous ? 'anonymous-player' : 'regular-player'}>
                      {getPlayerDisplayName(game.playerA)}
                      {game.playerA.is_guest && (
                        <span className="guest-badge">👤</span>
                      )}
                    </span>
                    {' vs '}
                    <span className={game.playerB.is_anonymous ? 'anonymous-player' : 'regular-player'}>
                      {getPlayerDisplayName(game.playerB)}
                      {game.playerB.is_guest && (
                        <span className="guest-badge">👤</span>
                      )}
                    </span>
                  </div>
                  <div className="game-score">
                    {game.actualFramesPlayerA} ({game.framesToPlay}) {game.actualFramesPlayerB}
                  </div>
                </Link>
                <div className="game-actions">
                  <Link to={`/play/${game.id}`} className="btn btn-primary btn-small">
                    {t('common.play')}
                  </Link>
                  <button 
                    onClick={() => confirmDelete(game.id)} 
                    className="btn btn-danger btn-small"
                    disabled={deleting}
                  >
                    {t('common.delete')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {finishedGames.length > 0 && (
        <div className="games-section">
          <h2>{t('games.history')}</h2>
          <div className="games-grid">
            {finishedGames.map(game => (
              <div key={game.id} className="game-card finished">
                <Link to={`/game/${game.id}`} className="game-link">
                  <div className="game-players">
                    <span className={game.playerA.is_anonymous ? 'anonymous-player' : 'regular-player'}>
                      {getPlayerDisplayName(game.playerA)}
                      {game.playerA.is_guest && (
                        <span className="guest-badge">👤</span>
                      )}
                    </span>
                    {' vs '}
                    <span className={game.playerB.is_anonymous ? 'anonymous-player' : 'regular-player'}>
                      {getPlayerDisplayName(game.playerB)}
                      {game.playerB.is_guest && (
                        <span className="guest-badge">👤</span>
                      )}
                    </span>
                  </div>
                  <div className="game-score">
                    {game.actualFramesPlayerA}:{game.actualFramesPlayerB}
                  </div>
                </Link>
                <div className="game-actions">
                  <Link to={`/game/${game.id}`} className="btn btn-secondary btn-small">
                    {t('games.viewGame')}
                  </Link>
                  {/* Finished games cannot be deleted or modified */}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>{t('games.deleteGame')}</h3>
            <p>{t('games.deleteGameConfirm')}</p>
            <div className="modal-actions">
              <button 
                onClick={() => handleDeleteGame(deleteConfirm)} 
                className="btn btn-danger"
                disabled={deleting}
              >
                {deleting ? t('common.deleting') : t('common.delete')}
              </button>
              <button 
                onClick={cancelDelete} 
                className="btn btn-secondary"
                disabled={deleting}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GamesList;

